// src/experiments/flowVariants.js
//
// The seam for testing a whole question flow. Flow content is data: the
// question engine loads
// `json/components/QuestionsEngine/<Category>/<Product>/Question<N>.json`.
// A flow variant is a sibling directory, e.g. `Vehicle/CarInsurance_v2`,
// chosen by an experiment arm. `product` never changes: it stays the switch
// key for PRODUCT_CONFIG, the stepper, the results route and the API
// mappers. Only the directory the questions come from does.
//
// To add a variant flow:
//   1. copy the product's directory to `<Product>_<suffix>` and edit it;
//   2. register the experiment in experiments.config.js with
//      `products: ['<Product>']`, so it targets that flow's URLs only;
//   3. map the arm to the directory in FLOW_VARIANTS below;
//   4. if the variant fires a different number of GTM steps, declare it in
//      QUESTION_SET_STEPS and keep `gtm.step_number` contiguous from 1;
//   5. run the tests: questionSets.test.js checks every directory.

import { getVariant } from './assignments';
import { recordExposure } from './exposure';

// experimentKey -> { product, arms: { arm: 'Category/Directory' } }
// Arms without an entry, always including control, load the product's own
// directory.
export const FLOW_VARIANTS = {
  // flow_v2: { product: 'CarInsurance', arms: { b: 'Vehicle/CarInsurance_v2' } },
};

// questionSet -> total GTM steps, only where it differs from the product's
// PRODUCT_CONFIG.totalQuoteSteps. quote_results_view and thank_you_view
// derive their step numbers from it, so a variant with a different count
// must be declared here or the two funnels stop lining up.
export const QUESTION_SET_STEPS = {
  // 'Vehicle/CarInsurance_v2': 6,
};

export function baseQuestionSet(category, product) {
  return `${category}/${product}`;
}

// Returns { questionSet, experimentKey }. experimentKey is set whenever an
// experiment decided the set for this visitor, including a control arm, so
// the engine can record the exposure.
export function resolveQuestionSet(
  category,
  product,
  variants = FLOW_VARIANTS
) {
  const questionSet = baseQuestionSet(category, product);
  if (!category || !product) return { questionSet, experimentKey: null };
  try {
    for (const [experimentKey, entry] of Object.entries(variants)) {
      if (!entry || entry.product !== product) continue;
      const arm = getVariant(experimentKey);
      if (arm === null) continue;
      const directory = entry.arms ? entry.arms[arm] : undefined;
      return {
        questionSet:
          typeof directory === 'string' && directory ? directory : questionSet,
        experimentKey,
      };
    }
  } catch {
    // fail open: the product's own flow
  }
  return { questionSet, experimentKey: null };
}

// Called by the question engine when it loads a step: that is where a tested
// flow actually renders. recordExposure dedupes per page view and ignores
// crawlers and unenrolled visitors.
export function recordFlowExposure(
  category,
  product,
  variants = FLOW_VARIANTS
) {
  const { experimentKey } = resolveQuestionSet(category, product, variants);
  if (experimentKey) recordExposure(experimentKey);
}

export function getQuestionSetTotalSteps(
  questionSet,
  steps = QUESTION_SET_STEPS
) {
  const value = questionSet ? steps[questionSet] : undefined;
  return Number.isInteger(value) && value > 0 ? value : null;
}

// Stored answers belong to the question set that collected them. A visitor
// who started this product's flow in one set and now resolves to another
// (a hard kill, or a returning session after a change) starts clean rather
// than landing in a flow that expects fields it never asked. Answers from a
// different product are kept: the app reuses personal details across flows
// on purpose.
export function shouldDiscardStoredFormValues(stored, current) {
  if (!stored || !current) return false;
  if (!stored.questionSet || !stored.product) return false;
  if (!current.questionSet || !current.product) return false;
  return (
    stored.product === current.product &&
    stored.questionSet !== current.questionSet
  );
}
